import type { ApiSchemas } from '@rental/contracts/client'
import { SITE_MODELS, type SiteModel } from '@rental/domain'

/**
 * Q-157: Categorie e modelli, the models that stand for each category. What
 * the screen shows is the server's map; this file only arranges it and writes
 * a model back whole, with the version read. The server decides what may be.
 */
export type StaffModel = ApiSchemas['StaffModel']
export type StaffModelCategory = ApiSchemas['StaffModelCategory']
export type StaffModels = ApiSchemas['StaffModels']
export type ModelUpdate = ApiSchemas['StaffModelUpdateInput']

/** The query every write reads again. */
export const MODELS_QUERY_KEY = ['models'] as const

const SITE = new Map(SITE_MODELS.map((model) => [model.ref, model]))

/** One of the website's models, by its id there; undefined for one it no longer has. */
export function siteModelOf(siteRef: string | null): SiteModel | undefined {
  return siteRef === null ? undefined : SITE.get(siteRef)
}

/** The model's name: its own for one added here, the website's for a site model. */
export function modelName(model: Pick<StaffModel, 'siteRef' | 'name'>): string {
  return model.name ?? siteModelOf(model.siteRef)?.name ?? model.siteRef ?? ''
}

/** Names the website gives two of its models (a car with a manual gearbox and the same car automatic). */
const NAMED_TWICE = new Set(
  SITE_MODELS.map((model) => model.name).filter((name, index, all) => all.indexOf(name) !== index),
)

/**
 * The model's name as the screen says it: a website name two models share
 * carries its gearbox ("Smart forfour (automatico)"), so the manual one is
 * never linked, made the lead or photographed for the other.
 */
export function modelLabel(
  model: Pick<StaffModel, 'siteRef' | 'name'>,
  gearboxes: Readonly<Record<'manual' | 'automatic', string>>,
): string {
  const name = modelName(model)
  const site = model.name === null ? siteModelOf(model.siteRef) : undefined
  if (!site?.transmission || !NAMED_TWICE.has(site.name)) return name
  return `${name} (${gearboxes[site.transmission].toLocaleLowerCase()})`
}

/** A site model's photo, as this app's build carries it (vite.config.ts, siteModelPhotoAssets). */
export function sitePhotoPath(photo: string): string {
  return `/site-models${photo}`
}

/** What a model shows: a photo taken here (fetched with the session), the site's, or none. */
export type ModelPicture =
  | { kind: 'media'; mediaId: string }
  | { kind: 'site'; src: string }
  | { kind: 'none' }

export function modelPicture(model: Pick<StaffModel, 'siteRef' | 'photoMediaId'>): ModelPicture {
  if (model.photoMediaId) return { kind: 'media', mediaId: model.photoMediaId }
  const site = siteModelOf(model.siteRef)
  return site ? { kind: 'site', src: sitePhotoPath(site.photo) } : { kind: 'none' }
}

export interface CategoryGroup {
  category: StaffModelCategory
  /** In use and linked here: the lead first, then by position, as the website lists them. */
  models: StaffModel[]
  lead: StaffModel | undefined
}

export interface ModelMap {
  groups: CategoryGroup[]
  /** In use, standing for no category. */
  unlinked: StaffModel[]
  /** Taken out of use (only a model added here can be). */
  retired: StaffModel[]
}

/** The server's lists arranged by category, in the categories' own order. */
export function arrange(data: StaffModels): ModelMap {
  const inUse = data.models.filter((model) => model.active)
  const order = (a: StaffModel, b: StaffModel) =>
    Number(b.isLead) - Number(a.isLead) ||
    a.position - b.position ||
    modelName(a).localeCompare(modelName(b), 'it')
  return {
    groups: data.categories.map((category) => {
      const models = inUse.filter((model) => model.categoryId === category.id).sort(order)
      return { category, models, lead: models.find((model) => model.isLead) }
    }),
    unlinked: inUse.filter((model) => model.categoryId === null).sort(order),
    retired: data.models.filter((model) => !model.active).sort(order),
  }
}

/** A website model that can be linked: one never added, or one added and now unlinked. */
export interface Linkable {
  siteRef: string
  name: string
  photo: string
  /** The model already in the map, unlinked; absent for one never added. */
  model?: StaffModel
}

/** The website's models free to link to a category, in the website's order. */
export function linkable(data: StaffModels): Linkable[] {
  const known = new Map(
    data.models.flatMap((model) => (model.siteRef ? [[model.siteRef, model] as const] : [])),
  )
  return SITE_MODELS.flatMap((site): Linkable[] => {
    const model = known.get(site.ref)
    if (model && (model.categoryId !== null || !model.active)) return []
    return [
      {
        siteRef: site.ref,
        name: site.name,
        photo: sitePhotoPath(site.photo),
        ...(model ? { model } : {}),
      },
    ]
  })
}

/** The next position in a category: after the last model listed there. */
export function nextPosition(group: CategoryGroup | undefined): number {
  return group ? Math.max(-1, ...group.models.map((model) => model.position)) + 1 : 0
}

/** A model written back whole: what it is, with the changes asked for. */
export function writeBack(
  model: StaffModel,
  changes: Partial<Omit<ModelUpdate, 'version'>> = {},
): ModelUpdate {
  const next: ModelUpdate = {
    version: model.version,
    categoryId: model.categoryId,
    isLead: model.isLead,
    position: model.position,
    photoMediaId: model.photoMediaId,
    active: model.active,
    ...changes,
  }
  // Out of a category, a model leads nothing; out of use, it stands for none.
  if (next.categoryId === null) next.isLead = false
  if (!next.active) {
    next.categoryId = null
    next.isLead = false
  }
  // A site model's name and details are the website's: never sent.
  if (model.siteRef !== null) {
    delete next.name
    delete next.seats
    delete next.transmission
    delete next.fuel
  }
  return next
}
